import type { ChipTone } from '@/design';
import { formatDateTime } from '@/domains/survey';
import type {
  DdListStatus,
  DdMeetingKind,
  IDdInterview,
  OutreachChannel,
  OutreachPurpose
} from './api/dd-contracts';
import type { IInternalFormSchema } from './api/internal-field-contracts';
import type { IListRow } from './api/lists-contracts';
import type { IOversightResponseRow } from './api/oversight-contracts';
import { recordPath } from './components/participant-link';
import { headlineSuffixOf } from './pages/person-hub/research-headline';
import { LIVE } from './recycle-bin';

/**
 * What the due-diligence screens share (survey-api's due-diligence design §6–§7): how a DD status
 * is said, the outreach vocabularies in words, and which of a survey's Quality rating options is a
 * score. survey-api decides every one of these; this module only words them.
 */

const DD_STATUS_CHIPS: Record<DdListStatus, { label: string; tone: ChipTone }> = {
  none: { label: 'Not started', tone: 'neutral' },
  in_progress: { label: 'In progress', tone: 'info' },
  pending_review: { label: 'Pending review', tone: 'attention' },
  completed: { label: 'Completed', tone: 'success' },
  cancelled: { label: 'Cancelled', tone: 'neutral' }
};

/** A status this table lacks, as spelled. */
export function ddStatusChip(status: string): { label: string; tone: ChipTone } {
  return DD_STATUS_CHIPS[status as DdListStatus] ?? { label: status, tone: 'neutral' };
}

/** The meeting kinds in words (SR-501), in the order they are offered. */
export const DD_MEETING_KINDS: ReadonlyArray<{ value: DdMeetingKind; label: string }> = [
  { value: 'dd_call', label: 'DD call' },
  { value: 'follow_up_call', label: 'Follow-up call' },
  { value: 'in_person_1', label: 'In-person interview — Round 1' },
  { value: 'in_person_2', label: 'In-person interview — Round 2' },
  { value: 'in_person_3', label: 'In-person interview — Round 3' }
];

/** The kinds a meeting other than the DD call may be — what *Log a meeting* and a meeting's
 *  switch offer. */
export const OTHER_MEETING_KINDS = DD_MEETING_KINDS.filter((entry) => entry.value !== 'dd_call');

/** The in-person rounds, as *Schedule in-person interview* offers them. */
export const IN_PERSON_ROUNDS = DD_MEETING_KINDS.filter((entry) => entry.value.startsWith('in_person_'));

/** A kind in words, one this table lacks as spelled. */
export function meetingLabel(kind: string): string {
  return DD_MEETING_KINDS.find((entry) => entry.value === kind)?.label ?? kind;
}

/** An interview's kind — an API before SR-501 sends none, and held only DD calls. */
export function meetingKindOf(interview: Pick<IDdInterview, 'kind'>): DdMeetingKind {
  return interview.kind ?? 'dd_call';
}

export const OUTREACH_CHANNELS: ReadonlyArray<{ value: OutreachChannel; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Call' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'other', label: 'Other' }
];

export const OUTREACH_PURPOSES: ReadonlyArray<{ value: OutreachPurpose; label: string }> = [
  { value: 'dd', label: 'DD call' },
  { value: 'metrics', label: 'Metrics' },
  { value: 'reinstatement', label: 'Reinstatement' },
  { value: 'other', label: 'Other' }
];

export function outreachWord(
  list: ReadonlyArray<{ value: string; label: string }>,
  value: string
): string {
  return list.find((entry) => entry.value === value)?.label ?? value;
}

/**
 * What the DD block's Disclosures line draws (SR-460), as its host already holds it — never read by
 * the block. Each fact is null where the host has not read it.
 */
export interface IDdDisclosures {
  /** No CRD to check disclosures for — said in place of the rest. */
  noCrd: boolean;
  /** Ever checked against a registry — true with no `checkedAt` where the check carries no date. */
  checked: boolean;
  /** The newest registry check's date, or null where there is none. */
  checkedAt: Nullable<string>;
  liveCount: Nullable<number>;
  /** The participant's unclosed work items in the compliance queue. */
  openItems: Nullable<number>;
  /** The CRD *Ask … again* asks about — null where none is held. */
  crd: Nullable<string>;
  /** A lookup on it is already waiting. */
  waiting: boolean;
  /** The record page, which draws the disclosures and the work items. */
  link: string;
}

/** The list fields a queue's row carries the Disclosures line in, asked beside a view's columns:
 *  the CRD, the newest check, the live count and the items open in compliance (absent at 0). */
export const DD_DISCLOSURE_COLUMNS = ['sys.crd', 'disc.checked_at', 'disc.live_count', 'disc.open_items'];

/** The Disclosures line's facts off a Lists row read with `DD_DISCLOSURE_COLUMNS`. */
export function ddDisclosuresOf(row: IListRow): IDdDisclosures {
  const text = (code: string) => {
    const v = row.cells[code]?.v;
    return typeof v === 'string' && v !== '' ? v : null;
  };
  const live = row.cells['disc.live_count']?.v;
  const open = row.cells['disc.open_items']?.v;
  const crd = text('sys.crd');
  const checkedAt = text('disc.checked_at');
  return {
    noCrd: crd === null && checkedAt === null,
    checked: checkedAt !== null,
    checkedAt,
    // Lists carry the live count only above 0: a checked row without one holds none live.
    liveCount: typeof live === 'number' ? live : checkedAt !== null ? 0 : null,
    openItems: typeof open === 'number' ? open : 0,
    crd,
    waiting: false,
    link: recordPath(row.participantId)
  };
}

/** The U4 flag (SR-498): what the newest registry check found, as a DD call needs it read —
 *  an issue where BrokerCheck holds a live disclosure or compliance holds an open item, clear
 *  where a check landed and found neither, else not checked; no CRD to check draws no flag (a
 *  team's or a firm's record, until a team is checked by its members' CRDs, SR-467). */
export type U4Flag = 'issue' | 'clear' | 'unchecked' | 'no_crd';

export function u4FlagOf(facts: IDdDisclosures): U4Flag {
  if ((facts.openItems ?? 0) > 0 || (facts.liveCount ?? 0) > 0) return 'issue';
  if (facts.noCrd) return 'no_crd';
  return facts.checked ? 'clear' : 'unchecked';
}

export const U4_FLAGS: Record<U4Flag, { label: string; tone: ChipTone }> = {
  issue: { label: 'U4: issue found', tone: 'attention' },
  clear: { label: 'U4: clear', tone: 'success' },
  unchecked: { label: 'U4: not checked', tone: 'neutral' },
  no_crd: { label: 'U4: no CRD', tone: 'neutral' }
};

/** What the issue is made of: the live disclosures and the items open in compliance, both
 *  counted — "2 disclosures (BrokerCheck), 1 open concern"; BrokerCheck's part said as unchecked
 *  where no check has landed, never as none found. */
export function u4IssueWords(facts: IDdDisclosures): string {
  const live = facts.liveCount;
  const open = facts.openItems ?? 0;
  const registry = live === null ? 'BrokerCheck not checked' : `${live} ${live === 1 ? 'disclosure' : 'disclosures'} (BrokerCheck)`;
  return `${registry}, ${open} open ${open === 1 ? 'concern' : 'concerns'}`;
}

/** WF-09's cadence: three emails, then a call. */
export const DD_EMAILS_BEFORE_CALL = 3;

/** The survey's Quality rating score options, in the field's order — a label starting with a
 *  digit, as survey-api tells a score from a reason. Empty where the survey binds no Quality
 *  rating the caller may see. */
export function scoreOptions(schema: IInternalFormSchema): Array<{ code: string; label: string }> {
  return (qualityField(schema)?.options ?? [])
    .filter((option) => /^\d/.test(option.label))
    .map((option) => ({ code: option.code, label: option.label }));
}

/** The survey's Quality rating reasons — every live option whose label does not start with a
 *  digit, in the field's order: survey-api's own test, which turns such a value into Travel NO. */
export function reasonOptions(schema: IInternalFormSchema): Array<{ code: string; label: string }> {
  return (qualityField(schema)?.options ?? [])
    .filter((option) => !option.retired && !/^\d/.test(option.label))
    .map((option) => ({ code: option.code, label: option.label }));
}

/**
 * What `ddStep` reads (SR-525): the advisor's DD state for one survey in the current cycle, as each
 * host holds it — the queue off its Lists row (`dd.status`, `dd.outreach_count`, `dd.scheduled_at`,
 * `dd.next_meeting_at` / `dd.next_meeting_kind`, `dd.disclosures_cleared_at`, the `disc.*`
 * columns), the DD block off its
 * interviews and outreach. A fact a host does not hold is left out, and the step reads it as the
 * cautious side: not cleared, U4 not known.
 */
export interface IDdStepState {
  /** The DD call's `dd.status` — `none` before any interview in the cycle. */
  status: string;
  /** DD outreach recorded this cycle, every channel (`dd.outreach_count`): WF-09's count. */
  outreach: number;
  /** The DD call's booking (`dd.scheduled_at`), null while none is held. */
  scheduledAt?: Nullable<string>;
  /** *Confirm clear for DD* recorded on this cycle's DD call (SR-460; a Lists row's
   *  `dd.disclosures_cleared_at`); absent or null where the host does not know, read as not
   *  cleared. */
  cleared?: Nullable<boolean>;
  /** The U4 flag (`u4FlagOf`) where the host holds the disclosures: `unchecked` makes the U4 step
   *  *Run U4 check* rather than *Confirm clear for DD*. */
  u4?: Nullable<U4Flag>;
  /** The soonest open booking of the cycle, of any kind (`dd.next_meeting_at` and its kind) — a
   *  `dd_call` here is the DD call's own booking, which `scheduledAt` already says. */
  nextMeeting?: Nullable<{ kind: DdMeetingKind; at: string }>;
  /** The clock, for "today" and "overdue"; `Date.now()` when absent. */
  now?: number;
}

/** The Lists cells `ddStepStateOf` reads, asked beside a view's columns. */
export const DD_STEP_COLUMNS = [
  'dd.status',
  'dd.outreach_count',
  'dd.scheduled_at',
  'dd.next_meeting_at',
  'dd.next_meeting_kind',
  'dd.disclosures_cleared_at'
];

/** A Lists row's soonest booking of any kind (A1), null while none is held. */
export function nextMeetingOf(row: IListRow): Nullable<{ kind: DdMeetingKind; at: string }> {
  const at = row.cells['dd.next_meeting_at']?.v;
  const kind = row.cells['dd.next_meeting_kind']?.v;
  return typeof at === 'string' && typeof kind === 'string' ? { kind: kind as DdMeetingKind, at } : null;
}

/** What `ddStep` reads off a Lists row read with `DD_STEP_COLUMNS` — the queue's and the chip's
 *  state alike, the clearance the panel reads off the interview too. The U4 flag is the caller's
 *  to add. */
export function ddStepStateOf(row: IListRow): IDdStepState {
  const booked = row.cells['dd.scheduled_at']?.v;
  const cleared = row.cells['dd.disclosures_cleared_at']?.v;
  return {
    status: String(row.cells['dd.status']?.v ?? 'none'),
    outreach: Number(row.cells['dd.outreach_count']?.v ?? 0) || 0,
    scheduledAt: typeof booked === 'string' ? booked : null,
    cleared: typeof cleared === 'string' && cleared !== '',
    nextMeeting: nextMeetingOf(row)
  };
}

/** What the step asks for — the panel's primary button does it, the queue and the chip say it. */
export type DdStepAction =
  | 'log_email'
  | 'log_call'
  | 'run_u4'
  | 'confirm_clear'
  | 'open_call'
  | 'open_meeting'
  | 'schedule_meeting';

export interface IDdStep {
  action: DdStepAction;
  /** The primary button's words (A3: the client's own). */
  label: string;
  /** The step in a few words, for a cell or a chip: "Email 2 of 3", "DD call today 5:00 PM". */
  summary: string;
  /** The meeting the step is about — the DD call or another booking — else null. */
  kind: Nullable<DdMeetingKind>;
  /** Its booking, else null. */
  at: Nullable<string>;
  /** The booking is today, still ahead (E2: a past one is only overdue). */
  today: boolean;
  /** The booking's time has passed with the meeting still to hold. */
  overdue: boolean;
}

/**
 * The advisor's one next step in DD (SR-525, A4) — the panel's primary button, *Next step* in the
 * DD calls queue and the record's DD chip all ask this, so the three never disagree:
 *
 * | State | Step |
 * |---|---|
 * | Not contacted, fewer than 3 DD attempts | Log email sent (n of 3) |
 * | 3 attempts, nothing booked | Log call attempt |
 * | DD call booked, not cleared, not today | Run U4 check / Confirm clear for DD |
 * | Booked and cleared, the call today or past, or the interview under way | Open DD call notes |
 * | Another meeting booked sooner than the DD call | Open meeting notes |
 * | DD call completed | the next booked meeting, else Schedule in-person interview |
 *
 * A booking stops the cadence: nobody emails an advisor who has a meeting in the diary (A1). Pure:
 * no read, no clock but `state.now`.
 */
export function ddStep(state: IDdStepState): IDdStep {
  const now = state.now ?? Date.now();
  const status = state.status;
  const call = status !== 'completed' ? (state.scheduledAt ?? null) : null;
  const other = state.nextMeeting && state.nextMeeting.kind !== 'dd_call' ? state.nextMeeting : null;
  const at = (instant: string) => ({ today: bookedToday(instant, now), overdue: passed(instant, now) });
  const unbooked = { kind: null, at: null, today: false, overdue: false };

  // Another meeting sooner than the DD call (or with none booked) is what happens next.
  if (other && (!call || Date.parse(other.at) < Date.parse(call))) {
    const when = at(other.at);
    return {
      action: 'open_meeting',
      label: 'Open meeting notes',
      summary: `${meetingShortLabel(other.kind)} ${ddWhenShort(other.at, now)}`,
      kind: other.kind,
      at: other.at,
      ...when
    };
  }
  if (status === 'completed') {
    return {
      action: 'schedule_meeting',
      label: 'Schedule in-person interview',
      summary: 'DD call done',
      ...unbooked
    };
  }
  if (call) {
    const when = at(call);
    const summary = `DD call ${ddWhenShort(call, now)}`;
    const base = { kind: 'dd_call' as const, at: call, ...when };
    if (state.cleared || when.today || when.overdue || status === 'pending_review') {
      return { action: 'open_call', label: 'Open DD call notes', summary, ...base };
    }
    return state.u4 === 'unchecked'
      ? { action: 'run_u4', label: 'Run U4 check', summary: `Check U4 · ${summary}`, ...base }
      : { action: 'confirm_clear', label: 'Confirm clear for DD', summary: `Check U4 · ${summary}`, ...base };
  }
  // An interview under way with no booking — notes typed, or awaiting review — is finished in it.
  if (status === 'in_progress' || status === 'pending_review') {
    return { action: 'open_call', label: 'Open DD call notes', summary: 'Finish the DD call', ...unbooked, kind: 'dd_call' };
  }
  if (state.outreach < DD_EMAILS_BEFORE_CALL) {
    const n = state.outreach + 1;
    return {
      action: 'log_email',
      label: `Log email sent (${n} of ${DD_EMAILS_BEFORE_CALL})`,
      summary: `Email ${n} of ${DD_EMAILS_BEFORE_CALL}`,
      ...unbooked
    };
  }
  return { action: 'log_call', label: 'Log call attempt', summary: 'Call', ...unbooked };
}

/**
 * The cadence's next step for a row of the DD list in its pre-SR-525 words — `ddStep` read for a
 * row that knows only its status, its count of DD outreach this cycle and the call's booking, so
 * the queue's *Next step* and My tasks keep saying what they said. `dated` false leaves the date
 * to a column beside it (DD calls' wide table).
 */
export function ddNextStep(
  status: string,
  outreach: number,
  scheduledAt?: Nullable<string>,
  dated = true
): string {
  if (status === 'completed') return 'Done';
  if (status === 'pending_review') return 'Finish the interview';
  const step = ddStep({ status, outreach, scheduledAt });
  if (step.at) {
    if (!dated) return step.overdue ? 'Call was due' : 'Call scheduled';
    return step.overdue ? `Call was due ${ddWhen(step.at)}` : `Call scheduled for ${ddWhen(step.at)}`;
  }
  if (step.action === 'open_call') return 'Finish the interview';
  return step.summary;
}

/** A kind in its short words, for a cell or a chip — "Round 1" beside "DD call". */
export function meetingShortLabel(kind: string): string {
  return kind.startsWith('in_person_') ? `Round ${kind.slice('in_person_'.length)}` : meetingLabel(kind);
}

/** An instant as a step says it: "today 5:00 PM" on the viewer's day, else the app's date. */
export function ddWhenShort(instant: string, now = Date.now()): string {
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return instant;
  if (sameDay(at, new Date(now))) {
    return `today ${at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  }
  return formatDateTime(instant);
}

/** Two instants on one day of the viewer's clock. */
export function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function passed(instant: string, now: number): boolean {
  const at = new Date(instant).getTime();
  return !Number.isNaN(at) && at < now;
}

/** On the viewer's today and still ahead — a past one is overdue only (E2). */
function bookedToday(instant: string, now: number): boolean {
  const at = new Date(instant);
  return !Number.isNaN(at.getTime()) && at.getTime() >= now && sameDay(at, new Date(now));
}

/** Whether a booked call's time has passed with the call still to make (SR-424) — the one reading
 *  this side makes of the date, to the minute against the viewer's clock, beside the work queue's
 *  "Overdue" by UTC day. A completed call, notes awaiting review or a cancelled meeting is never
 *  "due". */
export function callOverdue(scheduledAt: Nullable<string> | undefined, status: Nullable<string>): boolean {
  if (!scheduledAt || status === 'completed' || status === 'pending_review' || status === 'cancelled') return false;
  return passed(scheduledAt, Date.now());
}

/** An instant as the app says one, nothing for none. */
export function ddWhen(instant: Nullable<string>): string {
  return instant ? formatDateTime(instant) : '';
}

/** The cycle code of the newest live response — survey-api's own rule (the row's response), so
 *  "this cycle" here is the cycle the DD list reads. Null with no live response. */
export function currentCycle(responses: readonly IOversightResponseRow[]): Nullable<string> {
  return currentResponse(responses)?.cycle.code ?? null;
}

/** The newest live response — the one "this cycle" is — or null with none. */
export function currentResponse(responses: readonly IOversightResponseRow[]): Nullable<IOversightResponseRow> {
  const at = (row: IOversightResponseRow) => row.lockedAt ?? row.submittedAt ?? row.createdAt;
  return (
    responses
      .filter((row) => LIVE.has(row.status))
      .sort((a, b) => (at(a) < at(b) ? 1 : at(a) > at(b) ? -1 : a.responseId < b.responseId ? 1 : -1))[0] ?? null
  );
}

/** A Quality rating option code as its label, the code where the schema has none. */
export function qualityLabel(schema: Nullable<IInternalFormSchema>, code: Nullable<string>): string {
  if (!code) return '';
  return (schema ? qualityField(schema) : undefined)?.options.find((option) => option.code === code)?.label ?? code;
}

/** The survey's Quality rating field's code, null where the survey binds none the caller may
 *  see — the field an exception's reason is written into. */
export function qualityFieldCode(schema: IInternalFormSchema): Nullable<string> {
  return qualityField(schema)?.code ?? null;
}

/** The survey's Quality rating, by the headline suffix rule the record page and the queue read. */
function qualityField(schema: IInternalFormSchema) {
  return schema.groups
    .flatMap((group) => group.fields)
    .find((field) => headlineSuffixOf(field.code) === 'quality_rating');
}

/** `datetime-local`'s value for an instant, in the viewer's clock, and back. */
export function toLocalInput(instant: Nullable<string>): string {
  if (!instant) return '';
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

export function fromLocalInput(value: string): Nullable<string> {
  if (!value) return null;
  const at = new Date(value);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}
